import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { getGroups, submitWaiver } from "../../services/RetreatServices";
import { Button, Form } from "react-bootstrap";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Spinner } from "react-bootstrap";
import AlertModal from "./AlertModal";
import { useAuth } from "../../auth/AuthContext";
import EventSupport from "../retreat/EventSupport";

export default function WaiverForm() {
  const [groupData, setGroupData] = useState(null);
  const [formDataList, setFormDataList] = useState([]);
  const [useTypedSignature, setUseTypedSignature] = useState(false);
  const [signatureMap, setSignatureMap] = useState({});
  const [isSigned, setIsSigned] = useState(false);
  const signatureRef = useRef(null);
  const [typedSignature, setTypedSignature] = useState("");
  const [searchParams] = useSearchParams();
  const [currentMemberIndex, setCurrentMemberIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const registrationID = searchParams.get("registrationID");
  const retreatID = searchParams.get("retreatID");

  const waiverImageUrl = `${process.env.PUBLIC_URL}/images/Retreat_Waiver_${retreatID}.png`;
  const memberIDFilter = searchParams.get("memberID");
  const filters = useMemo(
    () => ({ registrationID, retreatID, memberIDFilter }),
    [registrationID, retreatID, memberIDFilter]
  );
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState(null);
  const { user, userType, logout } = useAuth();
  const [eventDetails, setEventDetails] = useState(null);
  useEffect(() => {
    console.log("Filters:", filters);
    console.log("Registration ID:", registrationID);
    console.log("Retreat ID:", retreatID);
    console.log("Member ID Filter:", memberIDFilter);

    const fetchEventDetails = async () => {
      const eventData = await EventSupport.fetchEventData(retreatID);
      setEventDetails(eventData);
      console.log("Event eventDetails:", eventData);
    };

    fetchEventDetails();

    // Validate required parameters
    if (!registrationID || !retreatID) {
      console.error("Missing query parameters: registrationID or retreatID");
      return;
    }
    getGroups(filters)
      .then((data) => {
        if (data && data.length > 0) {
          setGroupData(data[0]);
          if (!data[0].groupMembers) {
            console.error("Group members data not found");
            return;
          }
          // Filter out deleted members
          const activeMembers = data[0].groupMembers.filter(
            (member) => !member.deleted
          );
          console.log("Active Members:", activeMembers);
          const signatureMap = {};
          if (memberIDFilter) {
            console.log("Member ID Filter:", memberIDFilter);
            console.log("Active Members:", activeMembers);
            const memberIndex = activeMembers.findIndex(
              (member) =>
                member.memberID.toString() === memberIDFilter.toString()
            );
            console.log("Member Index:", memberIndex);
            if (memberIndex >= 0) {
              signatureMap[memberIDFilter] = null;
              setCurrentMemberIndex(memberIndex);
            } else {
              console.error("Member not found in group data");
            }
          } else {
            setCurrentMemberIndex(0);
            activeMembers.forEach((member) => {
              signatureMap[member.memberID] = null;
            });
          }
          setSignatureMap(signatureMap);
        } else {
          console.error("Participant data not found");
        }
      })
      .catch((error) =>
        console.error("Error fetching participant data:", error)
      );
  }, [filters]);

  const updateSignatureMap = (memberID, signature) => {
    setSignatureMap((prevMap) => ({
      ...prevMap,
      [memberID]: signature,
    }));
  };
  const handleEnd = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setIsSigned(true);
      updateSignatureMap("0", {
        type: "drawn",
        value: signatureRef.current.toDataURL(),
      });
    }
  };

  const toggleSignatureType = () => {
    setUseTypedSignature((prev) => {
      if (!prev) {
        // Save drawn signature before switching
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
          setSignatureMap((prevMap) => ({
            ...prevMap,
            ["0"]: {
              type: "drawn",
              value: signatureRef.current.toDataURL(),
            },
          }));
          signatureRef.current?.clear();
        } else {
          // Save typed signature before switching
          const activeMembers = groupData.groupMembers.filter(
            (member) => !member.deleted
          );
          const currentMember = activeMembers[currentMemberIndex];
          setSignatureMap((prevMap) => ({
            ...prevMap,
            ["0"]: {
              type: "typed",
              value: typedSignature,
            },
          }));
        }
      }
      return !prev;
    });
    const activeMembers = groupData.groupMembers.filter(
      (member) => !member.deleted
    );
    const currentMember = activeMembers[currentMemberIndex];
    const signature = signatureMap["0"];
    //console.log("Current Signature:", signature);
    if (signature?.value) {
      setIsSigned(true);
    } else {
      setIsSigned(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage("");
    setError(null);
    setShowErrorModal(false);
    if ("G" === userType) {
      console.log("handleCancel Evt Reg Page, logout GUEST MODE");
      logout();
    }
    navigate("/"); // Redirect to home page after closing modal
    setLoading(false);
    setTypedSignature("");
  };

  const saveSignature = async () => {
    const activeMembers = groupData?.groupMembers.filter(
      (member) => !member.deleted
    );
    const currentMember = activeMembers?.[currentMemberIndex];
    updateSignatureMap("0", {
      type: useTypedSignature ? "typed" : "drawn",
      value: useTypedSignature
        ? typedSignature
        : signatureRef.current.toDataURL(),
    });
    if (!currentMember) return false;
    const formData = new FormData();
    formData.append("retreatID", retreatID);
    formData.append("registrationID", registrationID);
    formData.append("memberID", "0");
    let signature;
    if (useTypedSignature) {
      if (!typedSignature.trim()) {
        alert("Please type your name as a signature before proceeding.");
        return false;
      }
      signature = {
        memberID: "0",
        type: "typed",
        value: typedSignature.trim(),
      };
    } else {
      if (!signatureRef.current || signatureRef.current.isEmpty()) {
        console.error("Please sign the waiver before proceeding 1.");
        setError("Please sign the waiver before proceeding.");
        setShowErrorModal(true);
        return false;
      }
      signature = {
        memberID: "0",
        type: "drawn",
        value: signatureRef?.current?.toDataURL(),
      };
    }
    //Wait for the PDF to be generated before proceeding
    const pdfBlob = await generatePDF(signature);
    formData.append(
      "waiverPDF",
      pdfBlob,
      `${retreatID}_${registrationID}_${currentMember.firstName}_${currentMember.lastName}_waiver.pdf`
    );

    setFormDataList((prev) => [...prev, formData]);
    return true;
  };

  /**
   * Generates a PDF document from the waiver form content.
   *
   * @param {Object} currentMember - The current member data.
   * @param {string} signature - The signature data.
   * @returns {Promise<Blob|null>} - A promise that resolves to the generated PDF as a Blob, or null if an error occurs.
   *
   * @throws {Error} If there is an issue generating the PDF.
   */
  const generatePDF = async (signature) => {
    try {
      const pdf = new jsPDF("p", "mm", "a4");
      if (typeof window === "undefined" || typeof document === "undefined") {
        console.error("Document or window is not available.");
        return null;
      }
      const content = document.getElementById("waiver-content");

      if (!content) {
        console.error("Waiver content element not found");
        return null;
      }

      // Elements to hide during capture
      const hiddenElements = [
        "signature-switch",
        "signature-switch-label",
        // "draw-signature",
        // "typed-signature",
      ]
        .map((id) => document.getElementById(id))
        .filter(Boolean);

      // Hide elements before capture
      hiddenElements.forEach((el) => (el.style.display = "none"));

      // Capture form as an image with optimized scale
      const canvas = await html2canvas(content, { scale: 1.2, useCORS: true });

      // Restore visibility after rendering
      hiddenElements.forEach((el) => (el.style.display = "block"));

      // Convert to compressed image
      const imgData = canvas.toDataURL("image/jpeg", 0.6);

      // Add form content as an image to PDF
      pdf.addImage(imgData, "JPEG", 10, 10, 190, 0);

      // **Append Signature**
      {
        /*
      if (signature && typeof signature === "object" && "value" in signature) {
        if (signature.type === "drawn") {
          // Drawn signature from canvas
          pdf.addImage(signature.value, "PNG", 50, 250, 100, 30); // Adjust position as needed
        } else if (signature.type === "typed") {
          // Set handwriting-style font (using a standard script font)
          pdf.setFont("courier", "italic"); // Change to a more signature-like font if available
          pdf.setFontSize(18);

          // Add typed signature
          pdf.text(signature.value, 80, 260);

          // Add underline
          const textWidth = pdf.getTextWidth(signature.value); // Get text width for accurate underline
          pdf.setDrawColor(0, 0, 0); // Black color
          pdf.setLineWidth(0.5); // Thin line
          pdf.line(80, 262, 80 + textWidth, 262); // Draw underline just below text
        }
      }*/
      }

      pdf.compress = true; // Enable compression

      return pdf.output("blob");
    } catch (error) {
      console.error("Error generating PDF:", error);
      return null;
    }
  };
  const handleSubmit = async () => {
    if (!(await saveSignature())) {
      return;
    }

    try {
      const activeMembers = groupData.groupMembers.filter(
        (member) => !member.deleted
      );
      const currentMember = activeMembers[currentMemberIndex];
      const formData = new FormData();
      formData.append("retreatID", retreatID);
      formData.append("registrationID", registrationID);
      formData.append("memberID", "0");
      let signature = signatureMap["0"];
      if (!signature) {
        if (useTypedSignature) {
          if (typedSignature.trim().length === 0) {
            setError("Please sign the waiver before proceeding.");
            setShowErrorModal(true);
            setLoading(false);
            return;
          } else {
            signature = {
              memberID: "0",
              type: "typed",
              value: typedSignature.trim(),
            };
          }
        } else {
          if (!signatureRef.current || signatureRef.current.isEmpty()) {
            setError("Please sign the waiver before proceeding.");
            setShowErrorModal(true);
            setLoading(false);
            return;
          } else {
            signature = {
              memberID: "0",
              type: "drawn",
              value: signatureRef.current.toDataURL(),
            };
          }
        }
      }

      updateSignatureMap("0", signature);

      const pdfBlob = await generatePDF(signature);
      formData.append(
        "waiverPDF",
        pdfBlob,
        `${retreatID}_${registrationID}_${currentMember.firstName}_${currentMember.lastName}_waiver.pdf`
      );

      await submitWaiver(formData);
      // Show success alert using AlertModal

      setShowSuccessModal(true);
      setSuccessMessage(
        groupData.status === "Paid"
          ? "Waiver Form Submitted successfully!"
          : "Waiver Form Submitted. Proceed with Payment(if not paid) for confirmation."
      );
      setLoading(false);
      //navigate("/"); // Redirect to home page after submission
    } catch (err) {
      console.error("Submission error:", err);
      setError("Failed to submit waiver.");
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  if (!groupData) {
    return <p>Loading...</p>;
  }
  const activeMembers =
    groupData?.groupMembers?.filter((member) => !member.deleted) || [];
  const currentMember = activeMembers[currentMemberIndex];
  return (
    <div className="container mx-auto p-4 me-1">
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div>
          <div
            id="waiver-content"
            style={{
              width: "210mm",
              padding: "3mm",
              backgroundColor: "#fff",
              borderRadius: "8px",
              textAlign: "justify",
              fontFamily: "Arial, sans-serif",
              fontSize: "14px",
            }}
          >
            <img
              src={waiverImageUrl}
              alt="Waiver Form"
              style={{
                width: "100%",
                maxHeight: "150px",
                objectFit: "cover",
                marginBottom: "10px",
              }}
            />
            <h2
              style={{
                textAlign: "center",
                fontSize: "18px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              {eventDetails
                ? eventDetails.short_description
                : "Krupabhishekam Retreat "}{" "}
              Waiver Form
            </h2>
            <p style={{ textAlign: "center" }}>
              <strong>Event:</strong>{" "}
              {eventDetails
                ? eventDetails.short_description
                : "Krupabhishekam Retreat "}{" "}
              at {eventDetails ? eventDetails.location : ""} (hereinafter
              referred to as the “Retreat”)
            </p>
            <p style={{ textIndent: "30px", color: "#333" }}>
              I willingly accept all risks, known and unknown, associated with
              participating in retreat activities, including those related to
              parking and the use of any shuttle or transportation services.
              Also, I consent to use the contact information provided for
              retreat related communication purposes
            </p>
            <p style={{ textIndent: "30px", color: "#333" }}>
              I agree, for myself, my heirs, and assigns, to hold harmless and
              release any participating individuals, organizers,{" "}
              {eventDetails ? eventDetails.location : "Organizer"}, the
              Syro-Malabar Diocese of Chicago, and any other sponsoring
              organizations, including their respective subsidiaries,
              affiliates, officers, directors, trustees, secretaries,
              volunteers, employees, agents, representatives, successors, and
              licensees (collectively, "Released Parties"), from any and all
              losses, liabilities, expenses, causes of action, and claims for
              damages ("Claims) that I may now or in the future have. These
              Claims may arise directly or indirectly from my participation in
              retreat activities; the use of any parking locations arranged for
              the retreat; the use of any shuttle or other transportation
              services provided or arranged; or the condition of the property,
              facilities, or equipment used during the retreat. This release
              applies regardless of when, where, or how such Claims arise and
              whether they are caused by the negligence of the Releases Parties
              or otherwise. This includes, but not limited to, Claims related
              to:
            </p>
            <ol type="i" style={{ color: "#333" }}>
              <li>Any theft, loss, or disappearance of property.</li>
              <li>Bodily injury (fatal or non-fatal).</li>
              <li>Property damage.</li>
            </ol>
            <p style={{ textIndent: "30px", color: "#333" }}>
              Please be informed that the Food served at Retreat is prepared by
              outside vendors and may contain or come in contact with common
              allergens, such as dairy, eggs, wheat, soybeans, tree nuts,
              peanuts, fish, shellfish, or other. We advise the parents /
              responsible adults shall take caution before consuming / providing
              any food. St. Alphonsa Syro-Malabar Catholic Church and its agents
              are not responsible for any allergies caused by consuming the food
              served.
            </p>
            <p style={{ textIndent: "30px", color: "#333" }}>
              Also, I consent to use all the contact details provided during the
              registration for retreat related communications by the "Released
              Parties".
            </p>
            <p style={{ textIndent: "30px", color: "#333" }}>
              This agreement will be construed broadly to prevent claims and
              lawsuits to the maximum extent permissible under applicable law.
              Any provisions found to be void or unenforceable shall be modified
              or deleted to the minimum extent necessary to make them
              enforceable and shall not affect the enforceability of any other
              provisions.
            </p>

            <p style={{ textIndent: "30px", color: "#333" }}>
              I have read this agreement, fully understand its terms, and
              recognize that I have given up certain legal rights by signing it
              in consideration of being permitted to participate in retreat
              activities, and to use parking and any shuttle or transportation
              services. I sign this agreement for the following participants
              attending the retreat.
            </p>
            <strong style={{ color: "#333" }}>Participants : </strong>
            <br />
            {groupData.groupMembers && (
              <div style={{ margin: "16px 0" }}>
                {groupData.groupMembers
                  .filter((member) => !member.deleted)
                  .map((member, idx, arr) => {
                    const displayIdx = idx + 1;
                    const isLast = idx === arr.length - 1;
                    const isLineBreak = displayIdx % 4 === 0 && !isLast;
                    return (
                      <span key={member.memberID} style={{ color: "#333" }}>
                        {displayIdx}. {member.firstName} {member.lastName}
                        {!isLast && ", "}
                        {isLineBreak && <br />}
                      </span>
                    );
                  })}
              </div>
            )}

            <p style={{ color: "#333" }}>
              I sign this agreement voluntarily and without any inducement or
              duress.
              <br />
              <br />
              <strong>Name:</strong>{" "}
              <u>{`${groupData.firstName} ${groupData.lastName}`}</u>
              <br />
              <strong>Date:</strong> <u>{new Date().toLocaleDateString()}</u>
            </p>

            <div className="d-flex align-items-center mt-0 gap-2">
              <Form.Label className="mb-0">
                <strong>Signature : </strong>{" "}
              </Form.Label>
              {useTypedSignature ? (
                <Form.Control
                  type="text"
                  placeholder="Type your name"
                  id="typed-signature"
                  value={typedSignature}
                  onChange={(e) => {
                    setTypedSignature(e.target.value);
                    setIsSigned(true);
                    setSignatureMap((prevMap) => ({
                      ...prevMap,
                      ["0"]: {
                        type: "typed",
                        value: e.target.value,
                      },
                    }));
                  }}
                  className="border border-black rounded me-2 signature-input"
                  style={{
                    width: 300, // Ensure full width on mobile
                    height: 73,
                    fontFamily: "'Great Vibes', cursive", // Signature-style font
                    //fontFamily: "'Courier New', Courier, monospace",
                    fontSize: "20px", // Readable size
                    fontStyle: "italic", // Apply italic styling correctly
                    color: "black",
                    textDecoration: "underline",
                  }}
                />
              ) : (
                <SignatureCanvas
                  penColor="black"
                  penWidth={1}
                  id="draw-signature"
                  canvasProps={{
                    className:
                      "border border-black rounded me-2 signature-input",
                    width: 300, // Ensure full width on mobile
                    height: 70,
                  }}
                  ref={signatureRef}
                  onEnd={handleEnd} // Triggered when signing ends
                />
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 mt-1">
            <Form.Check
              type="switch"
              id="signature-switch"
              label="Toggle for Typed Signature"
              checked={useTypedSignature}
              onChange={() => {
                toggleSignatureType();
                setTypedSignature(""); // Clear typed signature on toggle
                if (signatureRef.current) signatureRef.current.clear(); // Clear drawn signature on toggle
                setIsSigned(false);
              }}
              className="mb-0"
              aria-label="Toggle between drawn and typed signature"
            />
            <p
              className={`mb-0 ${isSigned ? "text-green-600" : "text-red-600"}`}
            >
              {isSigned ? "✅ Signature captured!  " : "✍️ Please sign above. "}
            </p>
            {/* Buttons */}
            <Button
              variant="secondary"
              onClick={() => {
                if (signatureRef.current) signatureRef.current.clear();
                setTypedSignature("");
                setIsSigned(false);
              }}
              style={{ width: "100px" }}
            >
              Clear
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={!isSigned}
              style={{ width: "250px" }}
            >
              Submit Waiver Form
            </Button>
          </div>
        </div>
      )}
      {/* Success Modal */}
      <AlertModal
        show={showSuccessModal}
        title="Waiver Form Submitted Successfully"
        message={successMessage}
        type="success"
        details={{}} // or pass additional details if necessary
        onCancel={handleCloseModal}
      />
      {/* Error Modal */}
      <AlertModal
        show={showErrorModal}
        title="Error in Waiver Form Submission"
        type="error"
        message={error}
        details={{}}
        onCancel={handleCloseModal}
      />
    </div>
  );
}
